import { NonIdealState, Spinner } from '@blueprintjs/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { getRequest } from '../../services/request';

const FS_PREFIX = 'https://cdn.filestackcontent.com/';
const POLICY_EXPIRY_BUFFER_MS = 1_000;

const policyUrlInflight = new Map<string, Promise<string>>();
/** Signed URLs that already rendered successfully — kept for the SPA session */
const sessionLoadedUrls = new Map<string, string>();

const normalizeFilestackSrc = (src: string): string => {
  if (!src.startsWith(FS_PREFIX)) {
    return src;
  }
  return src.split('?')[0];
};

const getFilestackHandle = (src: string): string =>
  normalizeFilestackSrc(src).substring(FS_PREFIX.length);

const getSessionLoadedUrl = (src: string): string | undefined =>
  sessionLoadedUrls.get(normalizeFilestackSrc(src));

const rememberLoadedUrl = (src: string, signedUrl: string): void => {
  sessionLoadedUrls.set(normalizeFilestackSrc(src), signedUrl);
};

const getPolicyExpiryMs = (signedUrl: string): number | null => {
  try {
    const policyParam = signedUrl.split('policy=')[1]?.split('&')[0];
    if (!policyParam) {
      return null;
    }

    const decoded = JSON.parse(atob(policyParam)) as { expiry?: number };
    if (typeof decoded.expiry === 'number') {
      return decoded.expiry * 1000;
    }
  } catch {
    return null;
  }

  return null;
};

interface PolicyCacheEntry {
  signedUrl: string;
  expiresAt: number;
}

const policyUrlCache = new Map<string, PolicyCacheEntry>();

const getCachedPolicyUrl = (src: string): string | undefined => {
  const cacheKey = normalizeFilestackSrc(src);
  const entry = policyUrlCache.get(cacheKey);
  if (!entry) {
    return undefined;
  }

  if (Date.now() >= entry.expiresAt) {
    policyUrlCache.delete(cacheKey);
    return undefined;
  }

  return entry.signedUrl;
};

const getPendingPolicyUrl = (src: string): string | undefined =>
  getSessionLoadedUrl(src) || getCachedPolicyUrl(src);

const evictPolicyUrlCache = (src: string): void => {
  const cacheKey = normalizeFilestackSrc(src);
  policyUrlCache.delete(cacheKey);
  policyUrlInflight.delete(cacheKey);
};

const setCachedPolicyUrl = (src: string, signedUrl: string): void => {
  const cacheKey = normalizeFilestackSrc(src);
  const expiryMs = getPolicyExpiryMs(signedUrl);

  policyUrlCache.set(cacheKey, {
    signedUrl,
    expiresAt:
      expiryMs !== null
        ? expiryMs - POLICY_EXPIRY_BUFFER_MS
        : Date.now() + 60 * 60 * 1000,
  });
};

const fetchPolicyUrl = async (
  src: string,
  { bypassCache = false }: { bypassCache?: boolean } = {}
): Promise<string> => {
  const normalizedSrc = normalizeFilestackSrc(src);
  const sessionUrl = sessionLoadedUrls.get(normalizedSrc);
  if (sessionUrl) {
    return sessionUrl;
  }

  if (bypassCache) {
    evictPolicyUrlCache(src);
  } else {
    const cached = getCachedPolicyUrl(src);
    if (cached) {
      return cached;
    }
  }

  const inflight = policyUrlInflight.get(normalizedSrc);
  if (inflight) {
    return inflight;
  }

  const promise = (async () => {
    const handle = getFilestackHandle(src);
    const requestClient = await getRequest();
    const policyResponse = await requestClient.get(
      `/documents/policy/view/${handle}`
    );

    if (policyResponse.status !== 200) {
      throw new Error('Failed to load image policy');
    }

    const signedUrl = `${normalizedSrc}?policy=${policyResponse.data.policy}&signature=${policyResponse.data.signature}`;
    setCachedPolicyUrl(normalizedSrc, signedUrl);
    return signedUrl;
  })();

  policyUrlInflight.set(normalizedSrc, promise);

  try {
    return await promise;
  } finally {
    policyUrlInflight.delete(normalizedSrc);
  }
};

interface SecureImageProps {
  src: string;
  fallbackSrc?: string;
  small?: boolean;
  noLoading: boolean;
  noError?: boolean;
  className?: string;
  lazy?: boolean;
}
const SecureImage = ({
  src,
  fallbackSrc,
  small = false,
  noLoading,
  noError = false,
  className,
  ...props
}: SecureImageProps) => {
  const mountedRef = useRef(true);
  const retriedRef = useRef(false);
  const srcRef = useRef(src);
  srcRef.current = src;

  const [displayUrl, setDisplayUrl] = useState<string | undefined>(() => {
    if (!src) {
      return undefined;
    }
    if (!src.startsWith(FS_PREFIX)) {
      return src;
    }
    return getSessionLoadedUrl(src) || getPendingPolicyUrl(src);
  });
  const [error, setError] = useState<boolean>();
  const [useFallbackImage, setUseFallbackImage] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    retriedRef.current = false;
    setReloadKey(0);
    setUseFallbackImage(false);
    setError(false);

    if (!src) {
      setDisplayUrl('');
      return;
    }

    if (!src.startsWith(FS_PREFIX)) {
      console.warn('Secure image used with non-filestack src', src);
      setDisplayUrl(src);
      return;
    }

    const sessionUrl = getSessionLoadedUrl(src);
    if (sessionUrl) {
      setDisplayUrl(sessionUrl);
      return;
    }

    const pending = getPendingPolicyUrl(src);
    if (pending) {
      setDisplayUrl(pending);
      return;
    }

    let cancelled = false;

    fetchPolicyUrl(src)
      .then((signedUrl) => {
        if (!cancelled && mountedRef.current && srcRef.current === src) {
          setDisplayUrl(signedUrl);
        }
      })
      .catch(() => {
        if (!cancelled && mountedRef.current && srcRef.current === src) {
          if (fallbackSrc) {
            setUseFallbackImage(true);
          } else {
            setError(true);
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, [src, fallbackSrc]);

  const handleImageLoad = useCallback(() => {
    if (displayUrl && src?.startsWith(FS_PREFIX)) {
      rememberLoadedUrl(src, displayUrl);
    }
  }, [displayUrl, src]);

  const handleImageError = useCallback(() => {
    if (useFallbackImage) {
      return;
    }

    // Already displayed successfully this session — keep the cached pixels,
    // don't refetch policy when the signed URL expires on Filestack's side.
    if (getSessionLoadedUrl(src)) {
      return;
    }

    if (!src?.startsWith(FS_PREFIX)) {
      if (fallbackSrc) {
        setUseFallbackImage(true);
      } else {
        setError(true);
      }
      return;
    }

    if (!retriedRef.current) {
      retriedRef.current = true;
      const failedSrc = src;

      fetchPolicyUrl(failedSrc, { bypassCache: true })
        .then((signedUrl) => {
          if (mountedRef.current && srcRef.current === failedSrc) {
            setDisplayUrl(signedUrl);
            setReloadKey((key) => key + 1);
            setUseFallbackImage(false);
            setError(false);
          }
        })
        .catch(() => {
          if (mountedRef.current && srcRef.current === failedSrc) {
            if (fallbackSrc) {
              setUseFallbackImage(true);
            } else {
              setError(true);
            }
          }
        });
      return;
    }

    if (fallbackSrc) {
      setUseFallbackImage(true);
    } else {
      setError(true);
    }
  }, [src, fallbackSrc, useFallbackImage]);

  if (error && !noError) {
    if (small) {
      return (
        <div className={'my-2'}>
          <FontAwesomeIcon icon="lock" className={'pr-1'} />
          <span>Unable to Display Secure Image</span>
        </div>
      );
    }
    return (
      <NonIdealState
        className="my-2"
        icon="lock"
        description="Unable to Display Secure Image"
      />
    );
  }

  if (useFallbackImage && fallbackSrc) {
    return (
      // eslint-disable-next-line jsx-a11y/alt-text
      <img className={className} src={fallbackSrc} {...props} />
    );
  }

  if (!displayUrl) {
    if (noLoading) {
      return <div className={className} aria-hidden />;
    }
    return <Spinner className="my-2" />;
  }

  return (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img
      key={reloadKey}
      className={className}
      onLoad={handleImageLoad}
      onError={handleImageError}
      src={displayUrl}
      {...props}
    />
  );
};

export default memo(SecureImage);
